import React, { useState, useEffect, useRef } from 'react';
import { CreditCard, ChevronDown, Check } from 'lucide-react';

export const BANDEIRAS = [
  { id: 'mastercard', label: 'Mastercard' },
  { id: 'visa', label: 'Visa' },
  { id: 'elo', label: 'Elo' },
  { id: 'amex', label: 'American Express' },
  { id: 'outro', label: 'Outro' },
];

export const bandeiraLabel = (id) => BANDEIRAS.find(b => b.id === id)?.label || 'Cartão';

// Marcas simplificadas em SVG (proporção 3:2). Para usar os arquivos oficiais
// de cada bandeira, basta trocar o conteúdo de cada <svg> abaixo.
export function CardBrandIcon({ bandeira, width = 36, className = '' }) {
  const height = Math.round((width * 2) / 3);
  const common = { width, height, viewBox: '0 0 48 32', className: `flex-shrink-0 ${className}`, role: 'img' };

  if (bandeira === 'mastercard') {
    return (
      <svg {...common} aria-label="Mastercard">
        <rect x="0.5" y="0.5" width="47" height="31" rx="5" fill="#fff" stroke="#E2E8F0" />
        <circle cx="18" cy="16" r="9" fill="#EB001B" />
        <circle cx="30" cy="16" r="9" fill="#F79E1B" />
        <path d="M24 9.3A9 9 0 0 1 24 22.7A9 9 0 0 1 24 9.3Z" fill="#FF5F00" />
      </svg>
    );
  }
  if (bandeira === 'visa') {
    return (
      <svg {...common} aria-label="Visa">
        <rect x="0.5" y="0.5" width="47" height="31" rx="5" fill="#fff" stroke="#E2E8F0" />
        <text x="24" y="21.5" textAnchor="middle" fontSize="15" fontWeight="900" fontStyle="italic"
          fontFamily="Arial, Helvetica, sans-serif" fill="#1A1F71" letterSpacing="0.5">VISA</text>
      </svg>
    );
  }
  if (bandeira === 'elo') {
    return (
      <svg {...common} aria-label="Elo">
        <rect width="48" height="32" rx="5" fill="#111827" />
        <circle cx="14" cy="9" r="2.2" fill="#FFCB05" />
        <circle cx="21" cy="9" r="2.2" fill="#00A4E0" />
        <circle cx="28" cy="9" r="2.2" fill="#EF4123" />
        <text x="24" y="26" textAnchor="middle" fontSize="13" fontWeight="800"
          fontFamily="Arial, Helvetica, sans-serif" fill="#fff">elo</text>
      </svg>
    );
  }
  if (bandeira === 'amex') {
    return (
      <svg {...common} aria-label="American Express">
        <rect width="48" height="32" rx="5" fill="#016FD0" />
        <text x="24" y="20.5" textAnchor="middle" fontSize="10.5" fontWeight="900"
          fontFamily="Arial, Helvetica, sans-serif" fill="#fff" letterSpacing="0.6">AMEX</text>
      </svg>
    );
  }
  return (
    <span
      className={`inline-flex items-center justify-center rounded-md bg-slate-100 dark:bg-slate-800 text-slate-500 flex-shrink-0 ${className}`}
      style={{ width, height }}
    >
      <CreditCard size={Math.round(width * 0.5)} />
    </span>
  );
}

// Dropdown com ícone + nome (o <select> nativo não aceita imagens nas opções).
export function BandeiraSelect({ value, onChange, testId }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
        data-testid={testId}
        className="fin-input flex items-center justify-between gap-2 w-full text-left cursor-pointer"
      >
        <span className="flex items-center gap-2.5 min-w-0">
          <CardBrandIcon bandeira={value} width={30} />
          <span className="truncate">{bandeiraLabel(value)}</span>
        </span>
        <ChevronDown size={15} className={`text-slate-400 flex-shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <ul
          role="listbox"
          className="absolute z-30 mt-1.5 w-full min-w-[200px] p-1.5 rounded-2xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 shadow-xl space-y-0.5"
        >
          {BANDEIRAS.map(b => (
            <li key={b.id}>
              <button
                type="button"
                role="option"
                aria-selected={value === b.id}
                onClick={() => { onChange(b.id); setOpen(false); }}
                className={`w-full flex items-center justify-between gap-2 px-2.5 py-2 rounded-xl text-sm text-left transition-colors cursor-pointer ${
                  value === b.id
                    ? 'bg-indigo-50 dark:bg-indigo-950/50 text-indigo-700 dark:text-indigo-300 font-semibold'
                    : 'text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700/60'
                }`}
              >
                <span className="flex items-center gap-2.5">
                  <CardBrandIcon bandeira={b.id} width={32} />
                  <span>{b.label}</span>
                </span>
                {value === b.id && <Check size={14} className="text-indigo-500" />}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
